import type { TicketThreadEntry } from '@easyhelp/shared';
import Stack from '@mui/material/Stack';
import { useState } from 'react';
import { DetailSection } from './DetailSection';
import { groupContainingAnchor, groupThreadEntries } from './thread-groups';
import { ThreadEvent } from './ThreadEvent';
import { ThreadEventGroup } from './ThreadEventGroup';
import { ThreadMessage } from './ThreadMessage';

/** I gruppi aperti, più quello che contiene l'ancora (se c'è). */
function withAnchorGroup(open: ReadonlySet<string>, anchorGroup: string | null): ReadonlySet<string> {
  return anchorGroup === null || open.has(anchorGroup) ? open : new Set(open).add(anchorGroup);
}

/**
 * Thread in ordine cronologico (PRD-004 US-4): la segnalazione iniziale, le repliche dell'utente
 * (#42), le risposte del supporto, le note interne («Non visibile all'utente») e gli eventi di
 * sistema. I messaggi restano sempre in evidenza (`ThreadMessage`); due o più eventi consecutivi si
 * raccolgono in una riga espandibile (`ThreadEventGroup`, #120), un evento isolato resta una riga
 * sola (`ThreadEvent`). Il composer è sotto questo blocco, in `TicketDetailPanel`.
 *
 * `anchor` è l'ancora del link di una notifica (#70): il gruppo che contiene quella voce è aperto
 * già al primo render, così il pannello può scorrere fin lì e darle il focus.
 */
export function TicketThread({
  entries,
  code,
  anchor,
}: {
  entries: readonly TicketThreadEntry[];
  code: string;
  anchor?: string;
}) {
  const items = groupThreadEntries(entries);
  const anchorGroup = groupContainingAnchor(items, anchor);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => withAnchorGroup(new Set(), anchorGroup));
  // Una nuova ancora apre il suo gruppo nello stesso render (nessun passaggio con la voce nascosta);
  // quando l'ancora sparisce dall'URL i gruppi restano come sono.
  const [seenAnchor, setSeenAnchor] = useState(anchor);
  if (anchor !== seenAnchor) {
    setSeenAnchor(anchor);
    setOpen((current) => withAnchorGroup(current, anchorGroup));
  }

  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <DetailSection title="Thread">
      <Stack component="ol" spacing={2} sx={{ m: 0, p: 0 }}>
        {items.map((item) => {
          switch (item.kind) {
            case 'messaggio':
              return <ThreadMessage key={item.entry.id} entry={item.entry} code={code} />;
            case 'evento':
              return <ThreadEvent key={item.entry.id} entry={item.entry} />;
            case 'gruppo':
              return (
                <ThreadEventGroup
                  key={`gruppo:${item.id}`}
                  events={item.events}
                  open={open.has(item.id)}
                  onToggle={() => toggle(item.id)}
                />
              );
          }
        })}
      </Stack>
    </DetailSection>
  );
}
